import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../lib/api-client';

const decodeImage = async (src: string) => {
  const image = new Image();
  image.src = src;
  await image.decode().catch(() => undefined);
};

/**
 * Loads the General Settings letter head and decodes it before resolving, so
 * print pages can wait on `isReady` and never auto-print before the image paints.
 * A failed request falls back to the blank letter head space.
 */
export const useLetterHead = (enabled = true) => {
  const query = useQuery({
    queryKey: ['settings', 'letter-head'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: true; data: { letterHeadImageDataUrl: string | null } }>('/settings/letter-head');
      const imageUrl = response.data.data.letterHeadImageDataUrl;
      if (imageUrl) await decodeImage(imageUrl);
      return imageUrl;
    },
    staleTime: 5 * 60_000,
    retry: 1,
    enabled,
  });
  return { imageUrl: query.data ?? null, isReady: !enabled || !query.isPending };
};

/** Letter head image, or a 200px blank band for pre-printed stationery when none is set. */
export const PrintLetterHead = ({ imageUrl }: { imageUrl: string | null }) => (
  imageUrl
    ? <div className="print-letter-head"><img src={imageUrl} alt="Letter head" /></div>
    : <div className="print-letter-head is-blank" aria-hidden="true" />
);
